import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  Calendar,
  User,
  ArrowRight,
  CheckCircle,
  Clock,
  AlertCircle,
  HardHat,
  Shield,
  ClipboardCheck,
  Wrench
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import { Assessment } from "../../FolderContentsTypes";
import { useNavigate } from "react-router-dom";

interface ModernRiskCardProps {
  assessment: Assessment;
  onOpen: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
  projectId?: string;
  folderId?: string;
}

export const ModernRiskCard: React.FC<ModernRiskCardProps> = ({
  assessment,
  onOpen,
  onEdit,
  onDelete,
  className,
  projectId,
  folderId
}) => {
  const navigate = useNavigate();
  const status = assessment.status || "open";
  
  const handleCreateWorkPreparation = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (projectId && folderId) {
      navigate(`/arbetsberedning/${projectId}/${folderId}?sourceAssessmentId=${assessment.assessment_id || assessment.id}`);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use the onOpen handler which should be properly connected to navigation logic
    onOpen();
  };

  const getTypeInfo = () => {
    // Force refresh debug - use timestamp to ensure fresh logs
    const timestamp = new Date().toISOString().slice(11, 23);
    console.log(`🎯 [${timestamp}] MODERN RISK CARD - getTypeInfo debug:`, {
      cardName: assessment.name,
      id: assessment.id,
      type: assessment.type,
      assessment_id: assessment.assessment_id,
      typeOfType: typeof assessment.type,
      isSkrAssessment: assessment.type === 'skr_assessment',
      hasSkrInName: assessment.name?.toLowerCase().includes('skr'),
      expectedCase: assessment.type === 'skr_assessment' ? '✅ Should show SKR-bedömning' : '❌ Will show Risksammanställning'
    });
    
    // Explicit check with console output for each case
    if (assessment.type === 'skr_assessment') {
      console.log(`✅ [${timestamp}] SKR CASE MATCHED for "${assessment.name}"`);
    } else if (assessment.type === 'work_preparation') {
      console.log(`✅ [${timestamp}] WORK PREP CASE MATCHED for "${assessment.name}"`);
    } else {
      console.log(`❌ [${timestamp}] DEFAULT CASE for "${assessment.name}" - type was:`, assessment.type);
    }
    
    switch (assessment.type) {
      case 'skr_assessment':
        console.log(`✅ [${timestamp}] SKR CASE - Light blue styling for:`, assessment.name);
        return {
          icon: Shield,
          label: "SKR-bedömning",
          color: "text-blue-600",
          bgColor: "bg-blue-100/80", // Light blue
          borderColor: "border-blue-300"
        };
      case 'work_preparation':
        console.log(`✅ [${timestamp}] WORK PREP CASE - Light red styling for:`, assessment.name);
        return {
          icon: Wrench,
          label: "Arbetsberedning",
          color: "text-red-600",
          bgColor: "bg-red-100/80", // Light red
          borderColor: "border-red-300"
        };
      default:
        console.log(`❌ [${timestamp}] DEFAULT CASE - Indigo styling for risk_assessment:`, assessment.name);
        return {
          icon: FileText,
          label: "Risksammanställning",
          color: "text-indigo-600",
          bgColor: "bg-indigo-100/80",
          borderColor: "border-indigo-300"
        };
    }
  };
  
  const getStatusInfo = () => {
    switch (status) {
      case "designer-completed":
        return {
          icon: CheckCircle,
          label: "Design klar",
          color: "text-green-600",
          bgColor: "bg-green-50",
          borderColor: "border-green-200"
        };
      case "production-completed":
        return {
          icon: CheckCircle,
          label: "Produktion klar",
          color: "text-blue-600",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200"
        };
      case "basu-ready":
        return {
          icon: CheckCircle,
          label: "BASU-klar",
          color: "text-indigo-600",
          bgColor: "bg-indigo-50",
          borderColor: "border-indigo-200"
        };
      default:
        return {
          icon: Clock,
          label: "Pågående",
          color: "text-orange-600",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200"
        };
    }
  };

  const statusInfo = getStatusInfo();
  const typeInfo = getTypeInfo();
  const StatusIcon = statusInfo.icon;
  const TypeIcon = typeInfo.icon;

  return (
    <Card 
      className={cn(
        "group transition-all duration-300 cursor-pointer glassmorphism border border-border/50 hover:border-primary/30",
        "transform hover:scale-[1.02] hover:-translate-y-1 backdrop-blur-sm shadow-lg hover:shadow-xl",
        className
      )}
      onClick={onOpen}
    >
      <CardContent className="p-6 bg-gradient-to-br hero-dark-gradient">
        <div className="flex items-start justify-between">
          {/* Icon & Main Content */}
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className={cn(
              "flex-shrink-0 p-3 rounded-xl transition-all duration-300 backdrop-blur-sm border",
              typeInfo.bgColor,
              typeInfo.borderColor,
              `group-hover:${typeInfo.bgColor.replace('/30', '/40')}`
            )}>
              <TypeIcon className={cn("h-6 w-6", typeInfo.color)} />
            </div>
            
            <div className="min-w-0 flex-1 space-y-3">
              {/* Title & Status */}
              <div className="flex items-start gap-3 flex-wrap">
                <h3 className="font-semibold text-foreground text-base leading-tight">
                  {assessment.name}
                </h3>
                <div className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm",
                  statusInfo.bgColor,
                  statusInfo.color,
                  statusInfo.borderColor,
                  "border"
                )}>
                  <StatusIcon className="h-3 w-3" />
                  {statusInfo.label}
                </div>
              </div>
              
              {/* Description */}
              {assessment.description && (
                <p className="text-sm text-on-hero/80 line-clamp-2 leading-relaxed">
                  {assessment.description}
                </p>
              )}
              
              {/* Metadata */}
              <div className="flex items-center gap-4 text-xs text-on-hero/70">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    {formatDistanceToNow(new Date(assessment.created_at), { 
                      addSuffix: true, 
                      locale: sv 
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  <span>{typeInfo.label}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/20 backdrop-blur-sm"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/20 backdrop-blur-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {assessment.type === 'risk_assessment' && (
                  <DropdownMenuItem onClick={handleCreateWorkPreparation}>
                    <HardHat className="h-4 w-4 mr-2" />
                    Skapa arbetsberedning
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Redigera
                </DropdownMenuItem>
                {onDelete && (
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Ta bort
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};